// =============================================================================
// src/features/crisis/crisis-chart-geometry.ts
// Geometria SVG dos graficos do takeover de Crise (sparklines, area, medidor).
//
// Proveniencia: porte FIEL de assets/crisis.js 22-86 (buildPolyline/buildArea/
//   drawCharts). A serie `evo` (24h, baixa noturna + disparo ~07h apos a
//   publicacao) e os paddings de cada SVG sao mantidos byte-a-byte para paridade
//   visual. O medidor (gauge) usa getTotalLength/getPointAtLength, que retornam 0
//   com display:none — por isso drawCharts so roda com o takeover visivel.
//
// SRP: so calculo/atribuicao de geometria SVG. Sem listeners, sem timers, sem
//   estado de UI. A interacao de hover vive em crisis-evo-slider.ts.
// =============================================================================

import { qs } from '../../app/dom';

/** Coordenada [x, y] em unidades do viewBox da area de evolucao. */
export type EvoPlotPoint = readonly [x: number, y: number];

/**
 * Metadados da area de evolucao reaproveitados pelo slider interativo
 * (crisis-evo-slider.ts). Espelha o objeto devolvido por buildArea no legado.
 */
export interface EvoAreaGeometry {
  readonly step: number;
  readonly padX: number;
  readonly w: number;
  readonly h: number;
  readonly max: number;
  readonly coords: readonly EvoPlotPoint[];
}

/**
 * Serie de 24h da evolucao de mencoes (assets/crisis.js 65). Exportada para o
 * slider interativo (crisis-evo-slider.ts) consumir os mesmos valores no
 * tooltip — fonte unica, sem duplicar a serie (DRY).
 */
export const EVO_SERIES: readonly number[] = [
  22, 18, 16, 15, 14, 13, 12, 11, 12, 14, 16, 20, 38, 72, 96, 104, 98, 92, 96, 99, 102, 100, 103, 108,
];

/** Fracao preenchida do arco do medidor de risco (62/100 — assets/crisis.js 78). */
const GAUGE_FILL_FRACTION = 0.62;

/**
 * Desenha sparklines, area de evolucao e medidor; devolve a geometria da area
 * para o slider interativo ligar o hover. Idempotente via flag do chamador.
 */
export function drawCrisisCharts(): EvoAreaGeometry | null {
  buildPolyline('crzSpark1', [4, 5, 4, 6, 7, 6, 8, 12, 18, 26, 34], 58, 22, 3);
  buildPolyline('crzSpark2', [10, 11, 10, 12, 14, 16, 15, 18, 22, 26, 30], 58, 22, 3);
  const geometry = buildEvoArea();
  if (geometry) positionEvoMarker(geometry);
  fillGaugeArc();
  return geometry;
}

/** Atribui os pontos de uma sparkline `<polyline>` (assets/crisis.js 23-36). */
function buildPolyline(id: string, data: readonly number[], w: number, h: number, pad: number): void {
  const host = qs<SVGElement>(`#${id}`);
  if (!host) return;
  const line = qs<SVGPolylineElement>('polyline', host) ?? host;
  const max = Math.max(...data);
  const min = Math.min(...data);
  /* v8 ignore next -- sparklines fixas têm valores distintos (range≠0); || 1 defensivo p/ divisão por zero */
  const range = max - min || 1;
  const step = (w - pad * 2) / (data.length - 1);
  const points = data.map((v, i) => formatPoint(pad + i * step, h - pad - ((v - min) / range) * (h - pad * 2)));
  line.setAttribute('points', points.join(' '));
}

/** "x.x,y.y" com uma casa decimal (assets/crisis.js 33). */
function formatPoint(x: number, y: number): string {
  return `${x.toFixed(1)},${y.toFixed(1)}`;
}

/**
 * Desenha a linha + area preenchida da evolucao de 24h (assets/crisis.js 38-54,
 * 66). Paddings: padX 6, padTop 10, padBottom 4 sobre 600x132.
 */
function buildEvoArea(): EvoAreaGeometry | null {
  const line = qs<SVGPathElement>('#crzEvoLine');
  const area = qs<SVGPathElement>('#crzEvoArea');
  if (!line || !area) return null;
  const coords = computeEvoCoords(EVO_SERIES, 600, 132, 6, 10, 4);
  const d = coords.map((c, i) => `${i ? 'L' : 'M'}${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(' ');
  line.setAttribute('d', d);
  area.setAttribute('d', closeAreaPath(d, coords, 132, 4));
  const step = (600 - 6 * 2) / (EVO_SERIES.length - 1);
  return { step, padX: 6, w: 600, h: 132, max: Math.max(...EVO_SERIES), coords };
}

/** Coordenadas [x,y] da serie no viewBox (assets/crisis.js 41-48). */
function computeEvoCoords(
  data: readonly number[],
  w: number,
  h: number,
  padX: number,
  padTop: number,
  padBottom: number,
): readonly EvoPlotPoint[] {
  /* v8 ignore next -- EVO_SERIES tem max 108 (≠0); || 1 defensivo p/ divisão por zero */
  const range = Math.max(...data) || 1; // min fixo em 0 no legado
  const step = (w - padX * 2) / (data.length - 1);
  return data.map((v, i): EvoPlotPoint => [padX + i * step, h - padBottom - (v / range) * (h - padTop - padBottom)]);
}

/** Fecha a area ate a base, ligando ultimo->primeiro ponto (assets/crisis.js 52). */
function closeAreaPath(d: string, coords: readonly EvoPlotPoint[], h: number, padBottom: number): string {
  const first = coords[0];
  const last = coords[coords.length - 1];
  /* v8 ignore next -- coords vem de EVO_SERIES (24 pontos), nunca vazio; guarda defensiva CLAUDE.md rule 11 */
  if (!first || !last) return d;
  return `${d} L${last[0].toFixed(1)} ${h - padBottom} L${first[0].toFixed(1)} ${h - padBottom} Z`;
}

/** Alinha a linha tracejada da publicacao ao 13o ponto (07h12 — assets/crisis.js 67-71). */
function positionEvoMarker(geometry: EvoAreaGeometry): void {
  const mark = qs<SVGLineElement>('#crzEvoMark');
  if (!mark) return;
  const x = (geometry.padX + 12 * geometry.step).toFixed(1);
  mark.setAttribute('x1', x);
  mark.setAttribute('x2', x);
}

/**
 * Preenche o arco do medidor ate 62% e posiciona o ponto no fim do trecho.
 * getPointAtLength pode lancar sem layout — o arco preenchido ja comunica o
 * nivel, entao o erro e ignorado (assets/crisis.js 75-85).
 */
function fillGaugeArc(): void {
  const arc = qs<SVGPathElement>('#crzGaugeArc');
  const dot = qs<SVGCircleElement>('#crzGaugeDot');
  if (!arc || !dot) return;
  const total = arc.getTotalLength();
  arc.style.strokeDasharray = String(total);
  arc.style.strokeDashoffset = String(total * (1 - GAUGE_FILL_FRACTION));
  try {
    const p = arc.getPointAtLength(total * GAUGE_FILL_FRACTION);
    dot.setAttribute('cx', String(p.x));
    dot.setAttribute('cy', String(p.y));
  } catch {
    // Provenience: layout indisponivel (display:none) — arco preenchido basta.
  }
}
